import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowDown, ArrowLeft, ArrowUp, Pencil, Plus, Trash2 } from "lucide-react";
import { Bouton } from "@/composants/ui/bouton";
import { Carte, CorpsCarte, EnTeteCarte } from "@/composants/ui/carte";
import { Champ, ChampSelection, ChampTexte } from "@/composants/ui/champ";
import { Chargement, Erreur } from "@/composants/ui/etats";
import { Etiquette } from "@/composants/ui/etiquette";
import { messageDe, useChargement } from "@/lib/chargement";
import { horodatage } from "@/lib/libelles";
import {
  api,
  type ChampModele,
  type DefinitionModele,
  type ModeleDocument,
  type PermissionCatalogue,
  type SectionModele,
  type StatutVersionModele,
  type SourceChampLie,
  type TypeChamp,
  type VersionModele,
} from "@/lib/api";

/**
 * Un modèle de document et ses versions (ADR-0060, ticket #106).
 *
 * Le brouillon se compose ici : sections et champs s'ajoutent, se renomment, se
 * réordonnent et se retirent, puis l'ensemble s'enregistre d'un bloc. Le serveur
 * valide la définition et dit chaque faute en toutes lettres ; il refuse aussi
 * toute modification d'une version publiée ou retirée. Publier retire la version
 * précédente.
 *
 * Les clés des sections et des champs se génèrent à l'ajout et ne changent plus :
 * les fiches s'y rapporteront, le libellé se renomme sans rien casser.
 *
 * Une section peut porter une clé de visibilité (ticket #109), choisie parmi les
 * permissions du catalogue : l'écran des profils crée les clés et les donne.
 */
const TYPES_CHAMP: { valeur: TypeChamp; libelle: string }[] = [
  { valeur: "TEXTE_COURT", libelle: "Texte court" },
  { valeur: "TEXTE_LONG", libelle: "Texte long" },
  { valeur: "NOMBRE", libelle: "Nombre avec unité" },
  { valeur: "COTE_MM", libelle: "Cote en millimètres" },
  { valeur: "DATE", libelle: "Date" },
  { valeur: "OUI_NON", libelle: "Oui / non" },
  { valeur: "CHOIX_UNIQUE", libelle: "Choix unique" },
  { valeur: "CHOIX_MULTIPLE", libelle: "Choix multiple" },
  { valeur: "CHAMP_LIE", libelle: "Champ lié au dossier" },
  { valeur: "ZONE_MEDIAS", libelle: "Zone de photos et vidéos" },
  { valeur: "EMPLACEMENT_SIGNATURE", libelle: "Emplacement de signature" },
];

// Ce qu'un champ lié reprend du dossier (#108) ; aucune source ne porte de montant.
const SOURCES_CHAMP_LIE: { valeur: SourceChampLie; libelle: string }[] = [
  { valeur: "CLIENT", libelle: "Client" },
  { valeur: "ADRESSE_REALISATION", libelle: "Adresse de réalisation" },
  { valeur: "CODE_LOT", libelle: "Code lot" },
  { valeur: "CHARGE_AFFAIRES", libelle: "Chargé d'affaires" },
  { valeur: "CATEGORIE", libelle: "Catégorie" },
];

function libelleSource(source: SourceChampLie | undefined) {
  return SOURCES_CHAMP_LIE.find((s) => s.valeur === source)?.libelle ?? source ?? "";
}

const STATUTS: Record<StatutVersionModele, string> = {
  BROUILLON: "Brouillon",
  PUBLIEE: "Publiée",
  RETIREE: "Retirée",
};

function cle(prefixe: string) {
  const hasard =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID().replace(/-/g, "")
      : Math.random().toString(16).slice(2) + Date.now().toString(16);
  return `${prefixe}_${hasard.slice(0, 10)}`;
}

function deplacer<T>(liste: T[], index: number, sens: -1 | 1): T[] {
  const cible = index + sens;
  if (cible < 0 || cible >= liste.length) return liste;
  const copie = [...liste];
  [copie[index], copie[cible]] = [copie[cible], copie[index]];
  return copie;
}

export function PageModeleDocument() {
  const { id = "" } = useParams();
  const charge = useChargement((signal) => api.modeleDocument(id, signal), [id]);
  const catalogue = useChargement((signal) => api.catalogueDesPermissions(signal), []);
  const [courant, setCourant] = useState<ModeleDocument | null>(null);
  const [action, setAction] = useState<string | null>(null);
  const [renommage, setRenommage] = useState<string | null>(null);

  const modele = courant ?? charge.donnees;
  if (charge.erreur) return <Erreur message={charge.erreur} onReessayer={charge.recharger} />;
  if (catalogue.erreur)
    return <Erreur message={catalogue.erreur} onReessayer={catalogue.recharger} />;
  if (!modele || !catalogue.donnees) return <Chargement message="Chargement du modèle…" />;
  const permissions = catalogue.donnees;

  async function agir(travail: () => Promise<ModeleDocument>) {
    setAction(null);
    try {
      setCourant(await travail());
      return true;
    } catch (cause) {
      setAction(messageDe(cause));
      return false;
    }
  }

  const brouillon = modele.versions.find((v) => v.statut === "BROUILLON");
  const publiee = modele.versions.find((v) => v.statut === "PUBLIEE");

  return (
    <div className="flex flex-col gap-5">
      <Link
        to="/administration/modeles-document"
        className="inline-flex items-center gap-1 text-sm text-encre-douce hover:text-encre"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden />
        Modèles de document
      </Link>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="break-words text-xl font-semibold text-encre">{modele.nom}</h1>
          <p className="mt-1 flex flex-wrap items-center gap-2 text-sm text-encre-douce">
            <span>{modele.type_libelle}</span>
            <Etiquette ton={modele.actif ? "actif" : "neutre"}>
              {modele.actif ? "Modèle en usage" : "Modèle retiré"}
            </Etiquette>
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Bouton taille="compact" variante="discret" onClick={() => setRenommage(modele.nom)}>
            <Pencil className="h-3.5 w-3.5" aria-hidden />
            Renommer
          </Bouton>
          <Bouton
            taille="compact"
            onClick={() =>
              void agir(() => api.corrigerModeleDocument(modele.id, { actif: !modele.actif }))
            }
          >
            {modele.actif ? "Retirer le modèle" : "Remettre en usage"}
          </Bouton>
        </div>
      </div>
      {!modele.actif ? (
        <p className="text-sm text-encre-douce">
          Retiré, le modèle ne sert plus aux fiches nouvelles ; celles qui existent le gardent.
        </p>
      ) : null}
      {renommage !== null ? (
        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={async (evenement) => {
            evenement.preventDefault();
            if (await agir(() => api.corrigerModeleDocument(modele.id, { nom: renommage })))
              setRenommage(null);
          }}
        >
          <div className="min-w-0 flex-1">
            <Champ
              libelle="Nom du modèle"
              required
              maxLength={200}
              value={renommage}
              onChange={(e) => setRenommage(e.target.value)}
            />
          </div>
          <Bouton type="submit" variante="principal">
            Enregistrer le nom
          </Bouton>
          <Bouton type="button" variante="discret" onClick={() => setRenommage(null)}>
            Annuler
          </Bouton>
        </form>
      ) : null}
      {action ? <Erreur message={action} /> : null}

      {brouillon ? (
        <EditeurBrouillon
          // Une version nouvelle repart de son propre état.
          key={brouillon.id}
          version={brouillon}
          permissions={permissions}
          enregistrer={(definition) =>
            agir(() => api.enregistrerVersionModele(modele.id, brouillon.id, definition))
          }
          publier={async (definition) => {
            // Ce qui est à l'écran est ce qui se publie : le brouillon s'enregistre d'abord.
            if (
              !(await agir(() => api.enregistrerVersionModele(modele.id, brouillon.id, definition)))
            )
              return false;
            return agir(() => api.publierVersionModele(modele.id, brouillon.id));
          }}
        />
      ) : (
        <div>
          <Bouton onClick={() => void agir(() => api.ouvrirVersionModele(modele.id))}>
            <Plus className="h-4 w-4" aria-hidden />
            Ouvrir une version nouvelle
          </Bouton>
          <p className="mt-1 text-xs text-encre-douce">Elle part de la dernière version publiée.</p>
        </div>
      )}

      {publiee ? <ApercuVersion version={publiee} permissions={permissions} /> : null}

      <Carte>
        <EnTeteCarte>
          <h2 className="text-sm font-semibold text-encre">Versions</h2>
        </EnTeteCarte>
        <ul className="divide-y divide-filet">
          {modele.versions.map((version) => (
            <li
              key={version.id}
              id={`version-${version.numero}`}
              className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3"
            >
              <span className="text-sm font-medium text-encre">Version {version.numero}</span>
              <Etiquette ton={version.statut === "PUBLIEE" ? "actif" : "neutre"}>
                {STATUTS[version.statut]}
              </Etiquette>
              <span className="flex min-w-0 flex-1 flex-col text-xs text-encre-douce">
                <span>
                  ouverte le {horodatage(version.cree_le)}
                  {version.cree_par ? ` par ${version.cree_par}` : ""}
                </span>
                {version.publiee_le ? (
                  <span>
                    publiée le {horodatage(version.publiee_le)}
                    {version.publiee_par ? ` par ${version.publiee_par}` : ""}
                  </span>
                ) : null}
                {version.retiree_le ? (
                  <span>
                    retirée le {horodatage(version.retiree_le)}
                    {version.retiree_par ? ` par ${version.retiree_par}` : ""}
                  </span>
                ) : null}
              </span>
              {version.statut !== "RETIREE" ? (
                <Bouton
                  taille="compact"
                  variante="discret"
                  onClick={() => void agir(() => api.retirerVersionModele(modele.id, version.id))}
                >
                  Retirer la version {version.numero}
                </Bouton>
              ) : null}
            </li>
          ))}
        </ul>
      </Carte>
    </div>
  );
}

function EditeurBrouillon({
  version,
  permissions,
  enregistrer,
  publier,
}: {
  version: VersionModele;
  permissions: PermissionCatalogue[];
  enregistrer: (definition: DefinitionModele) => Promise<boolean>;
  publier: (definition: DefinitionModele) => Promise<boolean>;
}) {
  const [definition, setDefinition] = useState<DefinitionModele>(version.definition);
  const [modifiee, setModifiee] = useState(false);
  const [envoi, setEnvoi] = useState(false);

  function changer(sections: SectionModele[]) {
    setDefinition({ sections });
    setModifiee(true);
  }

  function changerSection(index: number, section: SectionModele) {
    changer(definition.sections.map((s, i) => (i === index ? section : s)));
  }

  async function geste(travail: () => Promise<boolean>) {
    setEnvoi(true);
    try {
      if (await travail()) setModifiee(false);
    } finally {
      setEnvoi(false);
    }
  }

  const sections = definition.sections;
  const visibilites = [
    { valeur: "", libelle: "Tout lecteur de la fiche" },
    ...permissions.map((p) => ({
      valeur: p.code,
      libelle: p.cle_de_visibilite ? `Clé : ${p.libelle}` : p.libelle,
    })),
  ];
  return (
    <Carte>
      <EnTeteCarte className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-encre">Version {version.numero}, en brouillon</h2>
        <span className="text-xs text-encre-douce">
          {modifiee ? "Modifications non enregistrées" : "Tout est enregistré"}
        </span>
      </EnTeteCarte>
      <CorpsCarte className="flex flex-col gap-4">
        {sections.length === 0 ? (
          <p className="text-sm text-encre-douce">
            Aucune section : un modèle se compose de sections, qui portent les champs.
          </p>
        ) : null}
        {sections.map((section, index) => (
          <section
            key={section.cle}
            aria-label={`Section ${index + 1}`}
            className="flex flex-col gap-3 rounded-[6px] border border-filet p-3"
          >
            <div className="flex flex-wrap items-end gap-2">
              <div className="min-w-0 flex-1">
                <Champ
                  libelle={`Titre de la section ${index + 1}`}
                  required
                  maxLength={200}
                  value={section.titre}
                  onChange={(e) => changerSection(index, { ...section, titre: e.target.value })}
                />
              </div>
              <Gestes
                nom={`la section ${index + 1}`}
                premier={index === 0}
                dernier={index === sections.length - 1}
                monter={() => changer(deplacer(sections, index, -1))}
                descendre={() => changer(deplacer(sections, index, 1))}
                retirer={() => changer(sections.filter((_, i) => i !== index))}
              />
            </div>
            <ChampSelection
              libelle={`Visible par, section ${index + 1}`}
              aide="Une permission que le lecteur doit porter ; l'écran des profils crée les clés."
              value={section.cle_visibilite ?? ""}
              options={visibilites}
              onChange={(e) => {
                const { cle_visibilite: _, ...sansCle } = section;
                changerSection(
                  index,
                  e.target.value ? { ...sansCle, cle_visibilite: e.target.value } : sansCle,
                );
              }}
            />
            {section.champs.map((champ, position) => (
              <EditeurChamp
                key={champ.cle}
                rang={position + 1}
                champ={champ}
                premier={position === 0}
                dernier={position === section.champs.length - 1}
                changer={(nouveau) =>
                  changerSection(index, {
                    ...section,
                    champs: section.champs.map((c, i) => (i === position ? nouveau : c)),
                  })
                }
                deplacer={(sens) =>
                  changerSection(index, {
                    ...section,
                    champs: deplacer(section.champs, position, sens),
                  })
                }
                retirer={() =>
                  changerSection(index, {
                    ...section,
                    champs: section.champs.filter((_, i) => i !== position),
                  })
                }
              />
            ))}
            <div>
              <Bouton
                taille="compact"
                onClick={() =>
                  changerSection(index, {
                    ...section,
                    champs: [
                      ...section.champs,
                      {
                        cle: cle("c"),
                        libelle: "",
                        type: "TEXTE_COURT",
                        obligatoire: false,
                        parametres: {},
                      },
                    ],
                  })
                }
              >
                <Plus className="h-4 w-4" aria-hidden />
                Ajouter un champ
              </Bouton>
            </div>
          </section>
        ))}
        <div className="flex flex-wrap gap-2">
          <Bouton onClick={() => changer([...sections, { cle: cle("s"), titre: "", champs: [] }])}>
            <Plus className="h-4 w-4" aria-hidden />
            Ajouter une section
          </Bouton>
        </div>
        <div className="flex flex-wrap gap-2 border-t border-filet pt-4">
          <Bouton disabled={envoi} onClick={() => void geste(() => enregistrer(definition))}>
            Enregistrer le brouillon
          </Bouton>
          <Bouton
            variante="principal"
            disabled={envoi}
            onClick={() => void geste(() => publier(definition))}
          >
            Publier la version {version.numero}
          </Bouton>
        </div>
        <p className="text-xs text-encre-douce">
          Publiée, la version sert aux fiches nouvelles et ne se modifie plus ; la version publiée
          avant elle est retirée.
        </p>
      </CorpsCarte>
    </Carte>
  );
}

function EditeurChamp({
  rang,
  champ,
  premier,
  dernier,
  changer,
  deplacer: bouger,
  retirer,
}: {
  rang: number;
  champ: ChampModele;
  premier: boolean;
  dernier: boolean;
  changer: (champ: ChampModele) => void;
  deplacer: (sens: -1 | 1) => void;
  retirer: () => void;
}) {
  const aChoix = champ.type === "CHOIX_UNIQUE" || champ.type === "CHOIX_MULTIPLE";
  return (
    <div
      role="group"
      aria-label={`Champ ${rang}`}
      className="flex flex-col gap-3 rounded-[6px] bg-filet/30 p-3"
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <Champ
          libelle={`Libellé du champ ${rang}`}
          required
          maxLength={200}
          value={champ.libelle}
          onChange={(e) => changer({ ...champ, libelle: e.target.value })}
        />
        <ChampSelection
          libelle={`Type du champ ${rang}`}
          required
          value={champ.type}
          options={TYPES_CHAMP}
          onChange={(e) => {
            const type = e.target.value as TypeChamp;
            changer({
              ...champ,
              type,
              parametres:
                type === "NOMBRE"
                  ? { unite: champ.parametres.unite ?? "" }
                  : type === "CHOIX_UNIQUE" || type === "CHOIX_MULTIPLE"
                    ? { options: champ.parametres.options ?? [] }
                    : type === "CHAMP_LIE"
                      ? { source: champ.parametres.source ?? "CLIENT" }
                      : {},
              // Un champ lié ne se saisit pas, un emplacement de signature n'a pas de
              // valeur : ni l'un ni l'autre n'est obligatoire.
              obligatoire:
                type === "CHAMP_LIE" || type === "EMPLACEMENT_SIGNATURE"
                  ? false
                  : champ.obligatoire,
            });
          }}
        />
        {champ.type === "NOMBRE" ? (
          <Champ
            libelle={`Unité du champ ${rang}`}
            required
            maxLength={20}
            placeholder="m², ml, kg…"
            value={champ.parametres.unite ?? ""}
            onChange={(e) => changer({ ...champ, parametres: { unite: e.target.value } })}
          />
        ) : null}
        {champ.type === "CHAMP_LIE" ? (
          <ChampSelection
            libelle={`Donnée du dossier du champ ${rang}`}
            required
            value={champ.parametres.source ?? "CLIENT"}
            options={SOURCES_CHAMP_LIE}
            onChange={(e) =>
              changer({ ...champ, parametres: { source: e.target.value as SourceChampLie } })
            }
          />
        ) : null}
        {aChoix ? (
          <ChampTexte
            libelle={`Options du champ ${rang}, une par ligne`}
            required
            rows={4}
            value={(champ.parametres.options ?? []).join("\n")}
            onChange={(e) =>
              changer({
                ...champ,
                parametres: { options: e.target.value.split("\n") },
              })
            }
          />
        ) : null}
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        {champ.type === "CHAMP_LIE" ? (
          <p className="text-sm text-encre-douce">Repris du dossier, il ne se saisit pas.</p>
        ) : champ.type === "EMPLACEMENT_SIGNATURE" ? (
          <p className="text-sm text-encre-douce">
            Les signatures attendues viennent du type de document.
          </p>
        ) : (
          <label className="flex items-center gap-2 text-sm text-encre">
            <input
              type="checkbox"
              className="h-5 w-5"
              checked={champ.obligatoire}
              onChange={(e) => changer({ ...champ, obligatoire: e.target.checked })}
            />
            Obligatoire
          </label>
        )}
        <Gestes
          nom={`le champ ${rang}`}
          premier={premier}
          dernier={dernier}
          monter={() => bouger(-1)}
          descendre={() => bouger(1)}
          retirer={retirer}
        />
      </div>
    </div>
  );
}

function Gestes({
  nom,
  premier,
  dernier,
  monter,
  descendre,
  retirer,
}: {
  nom: string;
  premier: boolean;
  dernier: boolean;
  monter: () => void;
  descendre: () => void;
  retirer: () => void;
}) {
  return (
    <span className="flex flex-wrap items-center gap-1">
      <Bouton
        taille="compact"
        variante="discret"
        aria-label={`Monter ${nom}`}
        disabled={premier}
        onClick={monter}
      >
        <ArrowUp className="h-4 w-4" aria-hidden />
      </Bouton>
      <Bouton
        taille="compact"
        variante="discret"
        aria-label={`Descendre ${nom}`}
        disabled={dernier}
        onClick={descendre}
      >
        <ArrowDown className="h-4 w-4" aria-hidden />
      </Bouton>
      <Bouton taille="compact" variante="discret" aria-label={`Retirer ${nom}`} onClick={retirer}>
        <Trash2 className="h-4 w-4" aria-hidden />
      </Bouton>
    </span>
  );
}

/** La version publiée, telle que les fiches nouvelles la rempliront. */
function ApercuVersion({
  version,
  permissions,
}: {
  version: VersionModele;
  permissions: PermissionCatalogue[];
}) {
  const libelles = Object.fromEntries(TYPES_CHAMP.map((t) => [t.valeur, t.libelle]));
  const cles = Object.fromEntries(permissions.map((p) => [p.code, p.libelle]));
  return (
    <Carte>
      <EnTeteCarte>
        <h2 className="text-sm font-semibold text-encre">Version {version.numero}, en usage</h2>
      </EnTeteCarte>
      <CorpsCarte className="flex flex-col gap-4">
        {version.definition.sections.map((section) => (
          <div key={section.cle} className="flex flex-col gap-1">
            <h3 className="text-sm font-semibold text-encre">{section.titre}</h3>
            <p className="text-xs text-encre-douce">
              {section.cle_visibilite
                ? `Visible avec « ${cles[section.cle_visibilite] ?? section.cle_visibilite} »`
                : "Visible de tout lecteur de la fiche"}
            </p>
            <ul className="flex flex-col gap-1 text-sm text-encre">
              {section.champs.map((champ) => (
                <li key={champ.cle} className="break-words">
                  {champ.libelle}
                  <span className="text-encre-douce">
                    {" "}
                    · {libelles[champ.type]}
                    {champ.parametres.unite ? ` (${champ.parametres.unite})` : ""}
                    {champ.parametres.options ? ` : ${champ.parametres.options.join(", ")}` : ""}
                    {champ.type === "CHAMP_LIE"
                      ? ` : ${libelleSource(champ.parametres.source)}`
                      : champ.type === "EMPLACEMENT_SIGNATURE"
                        ? ""
                        : champ.obligatoire
                        ? " · obligatoire"
                        : " · facultatif"}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </CorpsCarte>
    </Carte>
  );
}
